import { useEffect, useMemo, useState } from "react";
import { ADMIN_ALL_LECTURES, ADMIN_LECTURE } from "../../../Api/Api";
import { Axios } from "../../../Api/axios";
import { Link } from "react-router-dom";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TransformDate from "../../../helpers/TransformDate";
import { ADMIN_LECTURE_FILE } from "../../../Api/Api";
import DashSelect from "../../../Components/Dashboard/DashSelect";

/* ── helpers ── */
function formatDate(value) {
  const d = new Date(value);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("ar-EG", { year: "numeric", month: "short", day: "numeric" });
}

const FILE_META = {
  pdf:   { color: "#3b82f6", bg: "#eff6ff", icon: "📄", label: "PDF" },
  video: { color: "#8b5cf6", bg: "#f5f3ff", icon: "🎬", label: "فيديو" },
  exam:  { color: "#f59e0b", bg: "#fffbeb", icon: "📝", label: "اختبار" },
};

async function openFile(filename) {
  try {
    const res = await Axios.get(ADMIN_LECTURE_FILE(filename), { responseType: "blob" });
    window.open(window.URL.createObjectURL(res.data), "_blank", "noopener,noreferrer");
  } catch { alert("تعذر فتح الملف"); }
}

function FileCell({ colKey, item }) {
  const meta = FILE_META[colKey];
  const fileVal = item[colKey];
  const urlVal  = item[`${colKey}_url`];
  if (urlVal) return (
    <a href={urlVal} target="_blank" rel="noopener noreferrer"
       className="file-pill" style={{ color: meta.color, background: meta.bg }}>
      <span>{meta.icon}</span> {meta.label}
    </a>
  );
  if (fileVal) return (
    <button type="button" className="file-pill file-pill-btn"
            style={{ color: meta.color, background: meta.bg }}
            onClick={() => openFile(fileVal)}>
      <span>{meta.icon}</span> {meta.label}
    </button>
  );
  return <span style={{ color: "#d1d5db" }}>—</span>;
}

function SkeletonRows({ cols }) {
  return Array.from({ length: 5 }).map((_, i) => (
    <tr key={i} className="skeleton-row">
      {Array.from({ length: cols }).map((_, j) => (
        <td key={j}><div className="skeleton-cell" style={{ width: j === 0 ? 36 : "70%" }} /></td>
      ))}
    </tr>
  ));
}

/* ── main component ── */
export default function Products() {
  const [lectures, setLectures] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search,  setSearch]    = useState("");
  const [date,    setDate]      = useState("");
  const [filterClass,   setFilterClass]   = useState("all");
  const [filterSubject, setFilterSubject] = useState("all");

  useEffect(() => {
    Axios.get(ADMIN_ALL_LECTURES)
      .then((res) => setLectures(res.data.lectures || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  /* build unique class & subject lists from data */
  const classes = useMemo(() => {
    const map = new Map();
    lectures.forEach((l) => {
      const c = l.ClassSubject?.class_ref;
      if (c) map.set(c.id, c.name);
    });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [lectures]);

  const subjects = useMemo(() => {
    const map = new Map();
    lectures.forEach((l) => {
      const s = l.ClassSubject?.subject_ref;
      if (s) map.set(s.id, s.name);
    });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [lectures]);

  /* filtered list */
  const displayed = useMemo(() => {
    return lectures.filter((l) => {
      const cs          = l.ClassSubject;
      const className   = cs?.class_ref?.name   || "";
      const subjectName = cs?.subject_ref?.name  || "";
      const classId     = cs?.class_ref?.id;
      const subjectId   = cs?.subject_ref?.id;

      if (filterClass   !== "all" && String(classId)   !== filterClass)   return false;
      if (filterSubject !== "all" && String(subjectId)  !== filterSubject) return false;
      if (date) {
        const lDate = TransformDate(l.createdAt);
        if (lDate !== date) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        if (!className.includes(q) && !subjectName.includes(q) && !String(l.id).includes(q))
          return false;
      }
      return true;
    });
  }, [lectures, filterClass, filterSubject, date, search]);

  async function handleDelete(id) {
    if (!window.confirm("هل أنت متأكد من حذف هذا الدرس؟")) return;
    try {
      await Axios.delete(ADMIN_LECTURE(id));
      setLectures((prev) => prev.filter((l) => l.id !== id));
    } catch (err) { console.error(err); }
  }

  const hasFilters = filterClass !== "all" || filterSubject !== "all" || date || search;
  const clearFilters = () => {
    setFilterClass("all");
    setFilterSubject("all");
    setDate("");
    setSearch("");
  };

  const COLS = 7; // #, مادة، صف، PDF، فيديو، اختبار، تاريخ، إجراءات

  return (
    <div className="dash-page">
      <div className="dash-page-header">
        <h1 className="dash-page-title">الدروس</h1>
        <Link className="dash-add-btn" to="/dashboard/product/add">
          <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
          </svg>
          إضافة درس جديد
        </Link>
      </div>

      <div className="table-card">
        {/* ── filters ── */}
        <div className="table-filters" style={{ flexWrap: "wrap", gap: 10 }}>

          {/* search */}
          <div className="filter-input-wrap" style={{ flex: "1 1 200px" }}>
            <svg className="filter-icon" width="15" height="15" fill="currentColor" viewBox="0 0 24 24">
              <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
            <input
              className="filter-input"
              type="search"
              placeholder="ابحث بالمادة أو الصف..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* filter by class */}
          <DashSelect
            value={filterClass}
            onChange={(v) => { setFilterClass(v); setFilterSubject("all"); }}
            options={[
              { value: "all", label: "جميع الصفوف" },
              ...classes.map((c) => ({ value: String(c.id), label: c.name })),
            ]}
            icon={
              <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
              </svg>
            }
          />

          {/* filter by subject */}
          <DashSelect
            value={filterSubject}
            onChange={setFilterSubject}
            options={[
              { value: "all", label: "جميع المواد" },
              ...subjects
                .filter((s) => {
                  if (filterClass === "all") return true;
                  return lectures.some(
                    (l) =>
                      String(l.ClassSubject?.class_ref?.id) === filterClass &&
                      l.ClassSubject?.subject_ref?.id === s.id
                  );
                })
                .map((s) => ({ value: String(s.id), label: s.name })),
            ]}
            icon={
              <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
              </svg>
            }
          />

          {/* date */}
          <input
            className="date-input"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />

          {/* clear + count */}
          {hasFilters && (
            <button className="stu-filter-clear" onClick={clearFilters}>
              <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
              مسح الفلاتر
            </button>
          )}
          <span className="stu-filter-count">{displayed.length} درس</span>
        </div>

        {/* ── table ── */}
        <div className="table-scroll">
          <table className="dash-table">
            <thead>
              <tr>
                <th>#</th>
                <th>المادة</th>
                <th>الصف</th>
                <th>PDF</th>
                <th>فيديو</th>
                <th>اختبار</th>
                <th>تاريخ الإضافة</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonRows cols={COLS} />
              ) : displayed.length === 0 ? (
                <tr>
                  <td colSpan={COLS + 1}>
                    <div className="table-state">
                      <svg width="48" height="48" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                      </svg>
                      <p>لا توجد دروس مطابقة</p>
                      {hasFilters && (
                        <button className="stu-filter-clear" style={{ margin: "8px auto 0" }} onClick={clearFilters}>
                          مسح الفلاتر
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                displayed.map((item, idx) => {
                  const cs          = item.ClassSubject;
                  const subjectName = cs?.subject_ref?.name || "—";
                  const className   = cs?.class_ref?.name   || "—";
                  return (
                    <tr key={item.id}>
                      <td><span className="id-badge">{item.id}</span></td>
                      <td>
                        <span style={{
                          display: "inline-block",
                          padding: "3px 10px",
                          borderRadius: 8,
                          background: "#f5f3ff",
                          color: "#7048e8",
                          fontWeight: 700,
                          fontSize: "0.82rem",
                        }}>
                          {subjectName}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          display: "inline-block",
                          padding: "3px 10px",
                          borderRadius: 8,
                          background: "#f0fdf4",
                          color: "#059669",
                          fontWeight: 700,
                          fontSize: "0.82rem",
                        }}>
                          {className}
                        </span>
                      </td>
                      <td><FileCell colKey="pdf"   item={item} /></td>
                      <td><FileCell colKey="video" item={item} /></td>
                      <td><FileCell colKey="exam"  item={item} /></td>
                      <td>{formatDate(item.createdAt)}</td>
                      <td>
                        <div style={{ display: "flex", gap: 8 }}>
                          <Link to={`${item.id}`} className="action-btn action-edit" title="تعديل">
                            <FontAwesomeIcon icon={faEdit} />
                          </Link>
                          <button
                            className="action-btn action-delete"
                            onClick={() => handleDelete(item.id)}
                            title="حذف"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
